import useSWRInfinite from 'swr/infinite';
import React from 'react';
import objectHash from 'object-hash';
import { AgentReview, AgentReviewsResponse, TemplateDataPagination } from '../../../providers/api/dtos';
import { getReviewsByAgentEmail, GetReviewsByAgentEmailFilters } from '../../../providers/api/marketing/templateDataProvider';
import { useMatchMutate } from '../../../utils/swr/swr';

export const AGENT_REVIEWS_RID = '/api/marketing/template-data/agent-reviews';

interface UseReviewsByAgentEmailInfiniteResult {
  data: AgentReview[],
  isLoading: boolean,
  isLoadingMore: boolean,
  hasMore: boolean,
  error: Error | undefined,
  loadMore: () => void,
  mutate: () => void,
}

export default function useReviewsByAgentEmail(
  filters?: GetReviewsByAgentEmailFilters,
  pagination?: TemplateDataPagination,
): UseReviewsByAgentEmailInfiniteResult {
  const matchMutate = useMatchMutate();

  const getKey = (_: number, previousPage: AgentReviewsResponse | null) => {
    if (previousPage && !previousPage.pageInfo.hasNext) return null;

    const endCursor = previousPage?.pageInfo.endCursor;
    return [AGENT_REVIEWS_RID, objectHash(filters ?? {}), objectHash(pagination ?? {}), endCursor];
  };

  const result = useSWRInfinite(getKey, ([_, __, ___, endCursor]) => getReviewsByAgentEmail({
    agentEmail: filters!.agentEmail,
    query: filters?.query,
  }, { pageSize: pagination?.pageSize, afterId: endCursor }));

  const allItems = result.data?.flatMap((page) => page.content) ?? [];
  const isLoadingMore = result.isValidating && (result.data?.length ?? 0) > 0;
  const lastPage = result.data?.at(-1)?.pageInfo;

  const loadMore = React.useCallback(() => {
    if (lastPage?.hasNext) {
      result.setSize((s) => s + 1);
    }
  }, [lastPage?.hasNext, result]);

  const mutate = React.useCallback(() => matchMutate(AGENT_REVIEWS_RID), [matchMutate]);

  return {
    data: allItems,
    isLoading: result.isLoading,
    isLoadingMore,
    error: result.error,
    hasMore: !!lastPage?.hasNext,
    loadMore,
    mutate,
  };
}
